import { CheckCircle2, Tag } from "lucide-react";
import { useState } from "react";
import { Button } from "~/components/ui/button";
import UpgradeModal from "~/components/upsell/UpgradeModal";

interface AIVisibilityUpsellBannerProps {
  trialDaysRemaining?: number | null;
  isTrialEnded?: boolean;
}

const trialEndedOutcomes = [
  {
    title: "Catch visibility drops early",
    description:
      "See the moment AI stops naming you — before it shows up as quieter demand.",
  },
  {
    title: "Know who is winning your answers",
    description:
      "Track which rivals get recommended on the prompts your buyers ask.",
  },
  {
    title: "Turn gaps into the next move",
    description:
      "Spot the questions where a better page or mention can flip the narrative.",
  },
];

const unlockOutcomes = [
  {
    title: "See how AI talks about you",
    description:
      "Learn whether ChatGPT, Claude, and others recommend you — or skip you.",
  },
  {
    title: "Find the answers competitors own",
    description:
      "Understand which buying questions put rivals ahead of your brand.",
  },
  {
    title: "Act on what actually moves the needle",
    description:
      "Prioritize the prompts and sources most likely to change how AI answers.",
  },
];

export default function AIVisibilityUpsellBanner({
  trialDaysRemaining = null,
  isTrialEnded = false,
}: AIVisibilityUpsellBannerProps) {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const isActiveTrial =
    trialDaysRemaining !== null && trialDaysRemaining > 0 && !isTrialEnded;
  const outcomes = isTrialEnded ? trialEndedOutcomes : unlockOutcomes;

  return (
    <>
      <div className="mx-auto max-w-lg rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg border border-stone-200 bg-stone-50">
          <Tag className="h-5 w-5 text-stone-700" />
        </div>

        <h3 className="text-xl font-semibold text-stone-900">
          {isTrialEnded
            ? "Trial expired"
            : isActiveTrial
              ? "You're on a free trial"
              : "Unlock AI Visibility"}
        </h3>

        <p className="pt-2 text-sm text-stone-500">
          {isTrialEnded
            ? "Your trial snapshot is still here — upgrade to keep monitoring how AI answers change, and stay ahead of competitors."
            : isActiveTrial
              ? `You have ${trialDaysRemaining} ${trialDaysRemaining === 1 ? "day" : "days"} left. Upgrade to keep watching without interruption.`
              : "See how AI platforms talk about your brand, where competitors win, and what to fix first."}
        </p>

        <div className="space-y-3 py-4">
          {outcomes.map((outcome) => (
            <div key={outcome.title} className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-stone-700" />
              <div>
                <div className="font-medium text-stone-900">{outcome.title}</div>
                <div className="text-sm text-stone-600">
                  {outcome.description}
                </div>
              </div>
            </div>
          ))}
        </div>

        <Button
          onClick={() => setUpgradeOpen(true)}
          className="w-full bg-orange-500 text-white hover:bg-orange-600"
        >
          {isTrialEnded
            ? "Subscribe"
            : isActiveTrial
              ? "Upgrade"
              : "Choose a plan"}
        </Button>
      </div>

      <UpgradeModal
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        icon={Tag}
        recommendedPlan="pro"
        title={
          isTrialEnded
            ? "Keep watching how AI talks about you"
            : "Upgrade your AI Visibility plan"
        }
        description="Pro keeps daily tracking live so you catch mention drops, competitor wins, and citation gaps before they cost you demand."
      />
    </>
  );
}
